import React, { useCallback, useEffect, useRef, useState } from "react";

import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf.mjs";
import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";

GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const Panel = styled.section`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    border-left: 1px solid #dbe4ec;
    background: #f6f9fc;
    overflow: hidden;
`;

const PdfHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 34px;
    padding: 0 8px;
    border-bottom: 1px solid #dfe7ef;
    background: #f8fafc;
`;

const HeaderTitle = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    color: #1c3145;
`;

const HeaderActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const PdfControlToolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 30px;
    padding: 0 8px;
    border-bottom: 1px solid #dfe7ef;
    background: #f8fafc;
`;

const ToolbarButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 20px;
    padding: 0 7px;
    border: 1px solid ${(props) => (props.$primary ? "#138064" : "#d7e1eb")};
    border-radius: 4px;
    background: ${(props) => (props.$primary ? "#138a6d" : props.$active ? "#eef4fb" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : "#405568")};
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const ToolbarLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    padding: 0 7px;
    border: 1px solid #d7e1eb;
    border-radius: 4px;
    background: #ffffff;
    color: #405568;
    font-size: 10px;
    font-weight: 600;
    text-decoration: none;
`;

const PageInput = styled.input`
    width: 44px;
    height: 20px;
    padding: 0 6px;
    border: 1px solid #d7e1eb;
    border-radius: 4px;
    background: #ffffff;
    color: #24384d;
    font-size: 10px;
    font-weight: 700;
    text-align: center;

    &:focus {
        outline: none;
        border-color: #9ab5d1;
    }
`;

const PageMeta = styled.div`
    font-size: 10px;
    color: #607489;
    font-weight: 600;
`;

const ZoomInput = styled.input`
    width: 48px;
    height: 20px;
    padding: 0 6px;
    border: 1px solid #d7e1eb;
    border-radius: 4px;
    background: #ffffff;
    color: #24384d;
    font-size: 10px;
    font-weight: 700;
    text-align: center;

    &:focus {
        outline: none;
        border-color: #9ab5d1;
    }
`;

const PdfViewport = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    padding: 10px;
    overflow: auto;
    background: #e7ecf2;
`;

const PageStack = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
`;

const PageShell = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const PageFrame = styled.div`
    width: ${(props) => `${props.$width}px`};
    min-width: 280px;
    border: 1px solid #d6dfeb;
    background: #ffffff;
    box-shadow: 0 8px 16px rgba(15, 23, 42, 0.07);
`;

const PageCanvas = styled.canvas`
    display: block;
    width: 100%;
    height: auto;
    background: #ffffff;
`;

const PagePlaceholder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 340px;
    color: #8194a8;
    font-size: 11px;
    background: #ffffff;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 32px;
    color: #5a6d82;
    text-align: center;
`;

const PdfFooterBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 28px;
    padding: 3px 8px;
    border-top: 1px solid #dfe7ef;
    background: ${(props) => (props.$failed ? "#fff6f6" : "#f4f8fc")};
`;

const PdfFooterBannerText = styled.div`
    min-width: 0;
    color: ${(props) => (props.$failed ? "#9a2c2c" : "#4f647a")};
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const PdfFooterBannerActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
`;

function ManuscriptPdfPanel({
    staleRemoteDocument,
    applyRemote,
    requestResync,
    latestCompile,
    pdfArtifact,
    logArtifact,
    onCompile,
    compileLoading,
    compileDisabled,
    currentCompilerLabel,
    currentRootPath,
}) {
    const [zoomMode, setZoomMode] = useState("fit-width");
    const [customScale, setCustomScale] = useState(1);
    const [pdfDocument, setPdfDocument] = useState(null);
    const [pdfPreviewError, setPdfPreviewError] = useState("");
    const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
    const [rendering, setRendering] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInput, setPageInput] = useState("1");
    const [zoomInput, setZoomInput] = useState("100");
    const [viewportSize, setViewportSize] = useState({ width: 800, height: 1040 });
    const [pageDimensions, setPageDimensions] = useState({});
    const [renderedPages, setRenderedPages] = useState([1, 2]);

    const viewportRef = useRef(null);
    const canvasRefs = useRef(new Map());
    const pageWrapRefs = useRef(new Map());
    const scrollRafRef = useRef(null);
    const renderTasksRef = useRef(new Map());

    const compileStatus = latestCompile?.status || "idle";
    const isCompileFailed = compileStatus === "failed";

    const resolveScale = useCallback(
        (baseViewport) => {
            if (zoomMode === "custom") return Math.max(0.2, customScale);
            if (zoomMode === "fit-page") {
                return Math.max(
                    0.2,
                    Math.min(
                        (viewportSize.width - 36) / baseViewport.width,
                        (viewportSize.height - 36) / baseViewport.height,
                    ),
                );
            }
            return Math.max(0.2, (viewportSize.width - 36) / baseViewport.width);
        },
        [customScale, viewportSize.height, viewportSize.width, zoomMode],
    );

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport || typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            setViewportSize({
                width: Math.max(320, Math.floor(entry.contentRect.width)),
                height: Math.max(420, Math.floor(entry.contentRect.height)),
            });
        });
        observer.observe(viewport);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        let cancelled = false;
        let loadingTask = null;

        const loadPreview = async () => {
            if (!pdfArtifact?.file_url) {
                setPdfDocument((prev) => {
                    prev?.destroy?.();
                    return null;
                });
                setPageCount(0);
                setCurrentPage(1);
                setPageInput("1");
                setRenderedPages([1, 2]);
                setPageDimensions({});
                setPdfPreviewLoading(false);
                setPdfPreviewError("");
                return;
            }

            setPdfPreviewLoading(true);
            setPdfPreviewError("");
            try {
                const response = await fetch(pdfArtifact.file_url);
                if (!response.ok) throw new Error(`PDF fetch failed: ${response.status}`);
                const arrayBuffer = await response.arrayBuffer();
                loadingTask = getDocument({ data: new Uint8Array(arrayBuffer) });
                const document = await loadingTask.promise;
                if (cancelled) {
                    document.destroy?.();
                    return;
                }
                setPdfDocument((prev) => {
                    prev?.destroy?.();
                    return document;
                });
                setPageCount(document.numPages || 0);
                setCurrentPage(1);
                setPageInput("1");
                setRenderedPages([1, 2]);
                setPageDimensions({});
            } catch (error) {
                if (!cancelled) {
                    setPdfDocument((prev) => {
                        prev?.destroy?.();
                        return null;
                    });
                    setPageCount(0);
                    setCurrentPage(1);
                    setPageInput("1");
                    setPdfPreviewError("PDF 미리보기를 불러오지 못했습니다.");
                }
            } finally {
                if (!cancelled) setPdfPreviewLoading(false);
            }
        };

        loadPreview();
        return () => {
            cancelled = true;
            loadingTask?.destroy?.();
        };
    }, [pdfArtifact?.file_url]);

    useEffect(() => {
        if (!pageCount) return;
        const next = new Set(renderedPages);
        for (let p = Math.max(1, currentPage - 2); p <= Math.min(pageCount, currentPage + 2); p += 1) {
            next.add(p);
        }
        const nextArray = Array.from(next).sort((a, b) => a - b);
        if (
            nextArray.length !== renderedPages.length ||
            nextArray.some((value, index) => value !== renderedPages[index])
        ) {
            setRenderedPages(nextArray);
        }
    }, [currentPage, pageCount, renderedPages]);

    useEffect(() => {
        if (!pdfDocument || !pageCount) return undefined;
        let cancelled = false;

        renderTasksRef.current.forEach((task) => {
            try {
                task?.cancel?.();
            } catch (error) {
                // noop
            }
        });
        renderTasksRef.current.clear();

        const render = async () => {
            setRendering(true);
            try {
                for (const pageNumber of renderedPages) {
                    if (cancelled) return;
                    const canvas = canvasRefs.current.get(pageNumber);
                    if (!canvas) continue;
                    const page = await pdfDocument.getPage(pageNumber);
                    if (cancelled) return;

                    const baseViewport = page.getViewport({ scale: 1 });
                    const scale = resolveScale(baseViewport);
                    const viewport = page.getViewport({ scale });
                    const ratio = window.devicePixelRatio || 1;
                    const context = canvas.getContext("2d", { alpha: false });

                    canvas.width = Math.floor(viewport.width * ratio);
                    canvas.height = Math.floor(viewport.height * ratio);
                    canvas.style.width = `${Math.floor(viewport.width)}px`;
                    canvas.style.height = `${Math.floor(viewport.height)}px`;
                    context.setTransform(ratio, 0, 0, ratio, 0, 0);

                    const renderTask = page.render({ canvasContext: context, viewport });
                    renderTasksRef.current.set(pageNumber, renderTask);
                    try {
                        await renderTask.promise;
                    } catch (error) {
                        if (error?.name !== "RenderingCancelledException") throw error;
                    } finally {
                        if (renderTasksRef.current.get(pageNumber) === renderTask) {
                            renderTasksRef.current.delete(pageNumber);
                        }
                    }
                    if (cancelled) return;

                    setPageDimensions((prev) => {
                        const old = prev[pageNumber];
                        const nextWidth = Math.max(280, Math.round(viewport.width));
                        const nextHeight = Math.max(360, Math.round(viewport.height));
                        if (old && old.width === nextWidth && old.height === nextHeight) return prev;
                        return { ...prev, [pageNumber]: { width: nextWidth, height: nextHeight } };
                    });
                }
            } finally {
                if (!cancelled) setRendering(false);
            }
        };

        render();
        return () => {
            cancelled = true;
            renderTasksRef.current.forEach((task) => {
                try {
                    task?.cancel?.();
                } catch (error) {
                    // noop
                }
            });
            renderTasksRef.current.clear();
        };
    }, [pageCount, pdfDocument, renderedPages, resolveScale]);

    const syncCurrentPageFromScroll = useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport || !pageCount) return;
        const viewportRect = viewport.getBoundingClientRect();
        const centerY = viewportRect.top + viewportRect.height / 2;
        let bestPage = currentPage;
        let bestDistance = Number.POSITIVE_INFINITY;
        for (const [page, element] of pageWrapRefs.current.entries()) {
            if (!element) continue;
            const rect = element.getBoundingClientRect();
            const distance = Math.abs(rect.top + rect.height / 2 - centerY);
            if (distance < bestDistance) {
                bestDistance = distance;
                bestPage = page;
            }
        }
        if (bestPage !== currentPage) {
            setCurrentPage(bestPage);
            setPageInput(String(bestPage));
        }
    }, [currentPage, pageCount]);

    const handlePdfViewportScroll = useCallback(() => {
        if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = requestAnimationFrame(syncCurrentPageFromScroll);
    }, [syncCurrentPageFromScroll]);

    useEffect(() => {
        return () => {
            if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
        };
    }, []);

    const scrollToPage = useCallback(
        (page) => {
            const nextPage = Math.min(Math.max(1, page), pageCount || 1);
            setCurrentPage(nextPage);
            setPageInput(String(nextPage));
            const node = pageWrapRefs.current.get(nextPage);
            if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
        },
        [pageCount],
    );

    const handlePageInputCommit = useCallback(() => {
        const parsed = Number.parseInt(pageInput, 10);
        if (!Number.isFinite(parsed)) {
            setPageInput(String(currentPage));
            return;
        }
        scrollToPage(parsed);
    }, [currentPage, pageInput, scrollToPage]);

    const handlePdfViewportWheel = useCallback((event) => {
        if (!(event.ctrlKey || event.metaKey)) return;
        event.preventDefault();
        setZoomMode("custom");
        setCustomScale((prev) => {
            const delta = event.deltaY > 0 ? -0.08 : 0.08;
            return Math.min(3, Math.max(0.4, Number((prev + delta).toFixed(2))));
        });
    }, []);

    const zoomOut = useCallback(() => {
        setZoomMode("custom");
        setCustomScale((prev) => Math.max(0.4, Number((prev - 0.1).toFixed(2))));
    }, []);

    const zoomIn = useCallback(() => {
        setZoomMode("custom");
        setCustomScale((prev) => Math.min(3, Number((prev + 0.1).toFixed(2))));
    }, []);

    const handleZoomInputCommit = useCallback(() => {
        const parsed = Number.parseInt(zoomInput, 10);
        if (!Number.isFinite(parsed)) {
            setZoomInput(String(Math.round(customScale * 100)));
            return;
        }
        const clamped = Math.min(300, Math.max(40, parsed));
        setZoomMode("custom");
        setCustomScale(Number((clamped / 100).toFixed(2)));
        setZoomInput(String(clamped));
    }, [customScale, zoomInput]);

    useEffect(() => {
        setRenderedPages((prev) => {
            const next = new Set(prev);
            for (let p = Math.max(1, currentPage - 2); p <= Math.min(pageCount, currentPage + 2); p += 1) {
                next.add(p);
            }
            return Array.from(next).sort((a, b) => a - b);
        });
    }, [currentPage, pageCount, zoomMode, customScale, viewportSize.width, viewportSize.height]);

    useEffect(() => {
        if (zoomMode === "custom") {
            setZoomInput(String(Math.round(customScale * 100)));
        }
    }, [customScale, zoomMode]);

    useEffect(() => {
        return () => {
            setPdfDocument((prev) => {
                prev?.destroy?.();
                return null;
            });
            renderTasksRef.current.forEach((task) => {
                try {
                    task?.cancel?.();
                } catch (error) {
                    // noop
                }
            });
            renderTasksRef.current.clear();
        };
    }, []);

    const footerBannerMessage = latestCompile?.error_message
        ? latestCompile.error_message
        : `상태: ${compileStatus}${latestCompile?.root_path ? ` · ${latestCompile.root_path}` : ""}`;

    return (
        <Panel>
            <PdfHeader>
                <HeaderTitle>
                    <Icon name={"pdf"} size={"14px"} color={"#0f8f6f"} />
                    PDF
                    <PageMeta>
                        {currentCompilerLabel}
                        {currentRootPath ? ` · ${currentRootPath}` : ""}
                    </PageMeta>
                </HeaderTitle>
                <HeaderActions>
                    <Tooltip message="다운로드">
                        {pdfArtifact?.file_url ? (
                            <ToolbarLink href={pdfArtifact.file_url} target="_blank" rel="noreferrer">
                                <Icon name={"download"} size={"12px"} color={"#6a7f97"} />
                            </ToolbarLink>
                        ) : (
                            <ToolbarButton type="button" disabled>
                                다운로드
                            </ToolbarButton>
                        )}
                    </Tooltip>
                    <ToolbarButton
                        type="button"
                        onClick={onCompile}
                        disabled={compileDisabled || compileLoading}
                        $primary
                    >
                        <Icon name={"play"} size={"10px"} color={"#ffffff"} />
                        {compileLoading ? "대기중" : "컴파일"}
                    </ToolbarButton>
                </HeaderActions>
            </PdfHeader>

            <PdfControlToolbar>
                <ToolbarButton
                    type="button"
                    onClick={() => scrollToPage(currentPage - 1)}
                    disabled={!pageCount || currentPage <= 1}
                >
                    <Icon name={"arrowLeft"} size={"10px"} color={"#405568"} />
                </ToolbarButton>
                <PageInput
                    value={pageInput}
                    onChange={(event) => setPageInput(event.target.value)}
                    onBlur={handlePageInputCommit}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            handlePageInputCommit();
                        }
                    }}
                />
                <PageMeta>/ {pageCount || 0}</PageMeta>
                <ToolbarButton
                    type="button"
                    onClick={() => scrollToPage(currentPage + 1)}
                    disabled={!pageCount || currentPage >= pageCount}
                >
                    <Icon name={"arrowRight"} size={"10px"} color={"#405568"} />
                </ToolbarButton>
                <ToolbarButton
                    type="button"
                    onClick={() => setZoomMode("fit-width")}
                    $active={zoomMode === "fit-width"}
                >
                    너비
                </ToolbarButton>
                <ToolbarButton type="button" onClick={() => setZoomMode("fit-page")} $active={zoomMode === "fit-page"}>
                    페이지
                </ToolbarButton>
                <ToolbarButton type="button" onClick={zoomOut}>
                    -
                </ToolbarButton>
                <ZoomInput
                    value={zoomInput}
                    onChange={(event) => setZoomInput(event.target.value)}
                    onBlur={handleZoomInputCommit}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            handleZoomInputCommit();
                        }
                    }}
                />
                <ToolbarButton type="button" onClick={zoomIn}>
                    +
                </ToolbarButton>
            </PdfControlToolbar>

            <PdfViewport ref={viewportRef} onScroll={handlePdfViewportScroll} onWheel={handlePdfViewportWheel}>
                {pdfDocument ? (
                    <PageStack>
                        {Array.from({ length: pageCount }, (_, index) => index + 1).map((pageNumber) => {
                            const dim = pageDimensions[pageNumber];
                            const shouldRenderCanvas = renderedPages.includes(pageNumber);
                            return (
                                <PageShell
                                    key={pageNumber}
                                    ref={(node) => {
                                        if (node) pageWrapRefs.current.set(pageNumber, node);
                                        else pageWrapRefs.current.delete(pageNumber);
                                    }}
                                >
                                    <PageFrame $width={dim?.width || 760}>
                                        {shouldRenderCanvas ? (
                                            <PageCanvas
                                                ref={(node) => {
                                                    if (node) canvasRefs.current.set(pageNumber, node);
                                                    else canvasRefs.current.delete(pageNumber);
                                                }}
                                            />
                                        ) : (
                                            <PagePlaceholder>Page {pageNumber}</PagePlaceholder>
                                        )}
                                    </PageFrame>
                                </PageShell>
                            );
                        })}
                    </PageStack>
                ) : (
                    <EmptyState>
                        <Icon name={"pdf"} size={"26px"} color={"#8ba0b5"} />
                        <div>
                            {pdfPreviewLoading
                                ? "PDF 미리보기를 불러오는 중입니다."
                                : pdfPreviewError
                                  ? pdfPreviewError
                                  : compileStatus === "queued" || compileStatus === "running"
                                    ? "compile 중입니다. PDF가 준비되면 여기에서 바로 확인할 수 있습니다."
                                    : "아직 PDF 미리보기가 없습니다. 컴파일을 눌러 생성하세요."}
                        </div>
                    </EmptyState>
                )}
                {rendering ? (
                    <EmptyState style={{ position: "absolute", inset: 0 }}>PDF 렌더링 중...</EmptyState>
                ) : null}
            </PdfViewport>

            <PdfFooterBanner $failed={isCompileFailed}>
                <PdfFooterBannerText $failed={isCompileFailed}>{footerBannerMessage}</PdfFooterBannerText>
                <PdfFooterBannerActions>
                    {logArtifact?.file_url ? (
                        <ToolbarLink href={logArtifact.file_url} target="_blank" rel="noreferrer">
                            로그
                        </ToolbarLink>
                    ) : null}
                    {staleRemoteDocument ? (
                        <>
                            <ToolbarButton type="button" onClick={applyRemote} $primary>
                                원격 버전 적용
                            </ToolbarButton>
                            <ToolbarButton type="button" onClick={requestResync}>
                                다시 불러오기
                            </ToolbarButton>
                        </>
                    ) : null}
                </PdfFooterBannerActions>
            </PdfFooterBanner>
        </Panel>
    );
}

export default ManuscriptPdfPanel;
